import { useEffect, useState } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { login, logout, session } from './api.js'
import { Banner, Button, Field } from './components.jsx'
import ErrorBoundary from './ErrorBoundary.jsx'
import { SailMark } from './brand.jsx'
import Dashboard from './pages/Dashboard.jsx'
import Reliability from './pages/Reliability.jsx'
import FailureAnalysis from './pages/FailureAnalysis.jsx'
import Cranes from './pages/Cranes.jsx'
import CraneDetail from './pages/CraneDetail.jsx'
import Complaints from './pages/Complaints.jsx'
import Incidents from './pages/Incidents.jsx'
import Admin from './pages/Admin.jsx'
import Users from './pages/Users.jsx'
import Vendors from './pages/Vendors.jsx'
import CraneEntry from './pages/CraneEntry.jsx'
import CraneImport from './pages/CraneImport.jsx'
import Escalation from './pages/Escalation.jsx'
import Inspections from './pages/Inspections.jsx'
import PlannedMaintenance from './pages/PlannedMaintenance.jsx'
import Reports from './pages/Reports.jsx'
import ApiDocs from './pages/ApiDocs.jsx'
import './app.css'

/**
 * The console shell: one sign-in, one navigation, every page inside it.
 *
 * **What this replaces.** Seven standalone HTML pages, each with its own login form and an in-memory token,
 * none of which linked back to the index — so administering the plant meant authenticating six times and
 * navigating by typing URLs. Measured before starting: 0 of 6 tool pages had a link home.
 *
 * **Role-aware, not role-faked.** The sidebar draws only what the signed-in role can actually use, and the
 * server independently refuses the rest — the JWT decode in `api.js` is for layout, never for authorization.
 * That matters because MANAGEMENT is read-only (backend V16): hiding the admin links is a courtesy, the 403
 * is the guarantee.
 */

const NAV = [
  {
    section: 'Overview',
    items: [
      { to: '/', label: 'Fleet dashboard', icon: 'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z', roles: null },
      { to: '/reliability', label: 'Reliability', icon: 'M3 17l6-6 4 4 8-8v6h-2V9.4l-6 6-4-4L3 19.8z', roles: null },
      { to: '/failures', label: 'Failure analysis', icon: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z', roles: null },
    ],
  },
  {
    section: 'Fleet',
    items: [
      { to: '/cranes', label: 'Cranes', icon: 'M4 4h16v2H4zm2 4h12l-2 12H8L6 8zm4 2v8h1v-8zm3 0v8h1v-8z', roles: null },
      { to: '/complaints', label: 'Complaints', icon: 'M20 2H4a2 2 0 00-2 2v18l4-4h14a2 2 0 002-2V4a2 2 0 00-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z', roles: null },
      { to: '/incidents', label: 'Safety incidents', icon: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm1 15h-2v-2h2v2zm0-4h-2V7h2v5z', roles: null },
      // Under Fleet, not Administration, and open to every role the console admits — `ReportController` allows
      // ADMIN, MANAGEMENT, SAFETY_OFFICER and both engineering disciplines, and exporting a register is a
      // read, not an administrative act. Ported from the legacy `/admin/reports.html`, which asked for a
      // separate login on every single download.
      { to: '/reports', label: 'Reports', icon: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z', roles: null },
      // Under Fleet rather than Administration, and open to every role the console admits — the register and
      // its downloads are readings of the plant's safety position, not an administrative tool. Scheduling
      // lives on the same page and the server refuses the write to anyone but safety and admin.
      { to: '/inspections', label: 'Inspections', icon: 'M19 3h-4.2A3 3 0 0012 1a3 3 0 00-2.8 2H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zm-7 0a1 1 0 110 2 1 1 0 010-2zm-1.2 14L7 13.2l1.4-1.4 2.4 2.4 4.8-4.8L17 10.8 10.8 17z', roles: null },
    ],
  },
  {
    section: 'Administration',
    items: [
      // ADMIN only. MANAGEMENT is read-only and every write endpoint behind these refuses it, so offering
      // the link would be offering a 403 — the same mistake M11 found on the Android side.
      { to: '/admin/import', label: 'Crane master import', icon: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z', roles: ['ADMIN'] },
      { to: '/admin/cranes', label: 'Single crane entry', icon: 'M4 4h16v2H4zm2 4h12l-2 12H8L6 8zm4 2v8h1v-8zm3 0v8h1v-8z', roles: ['ADMIN'] },
      { to: '/admin/users', label: 'User accounts', icon: 'M12 4a4 4 0 110 8 4 4 0 010-8zm0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4z', roles: ['ADMIN'] },
      { to: '/admin/vendors', label: 'Vendors', icon: 'M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 14H4v-6h16v6zm0-8H4V6h16v4z', roles: ['ADMIN'] },
      { to: '/admin/pm', label: 'Planned maintenance', icon: 'M12 2a10 10 0 100 20 10 10 0 000-20zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z', roles: ['ADMIN'] },
      { to: '/admin/escalation', label: 'Escalation & approvals', icon: 'M12 2L1 21h22L12 2zm1 16h-2v-2h2v2zm0-4h-2v-5h2v5z', roles: ['ADMIN'] },
      { to: '/admin/api', label: 'API documentation', icon: 'M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z', roles: null },
      { to: '/admin', label: 'Setup overview', icon: 'M19.4 13a7.6 7.6 0 000-2l2.1-1.6-2-3.5-2.5 1a7.7 7.7 0 00-1.7-1L15 3H9.9l-.3 2.9c-.6.3-1.2.6-1.7 1l-2.5-1-2 3.5L5.5 11a7.6 7.6 0 000 2l-2.1 1.6 2 3.5 2.5-1c.5.4 1.1.7 1.7 1l.3 2.9H15l.3-2.9c.6-.3 1.2-.6 1.7-1l2.5 1 2-3.5L19.4 13zM12.5 15a3 3 0 110-6 3 3 0 010 6z', roles: ['ADMIN'] },
    ],
  },
]

function Sidebar({ role, onSignOut }) {
  return (
    <aside className="sidebar">
      {/* The same lockup as the entrance, so the mark an administrator drops in appears in both places. */}
      <div className="brand">
        <SailMark height={30} />
      </div>

      <nav>
        {NAV.map((group) => {
          const items = group.items.filter((i) => !i.roles || i.roles.includes(role))
          if (!items.length) return null
          return (
            <div key={group.section} className="nav-group">
              <span className="nav-section">{group.section}</span>
              {items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.to === '/'} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d={i.icon} /></svg>
                  {i.label}
                </NavLink>
              ))}
            </div>
          )
        })}
      </nav>

      <div className="sidebar-foot">
        <div className="who">
          <strong>{session.name || session.staff}</strong>
          <span>{roleLabel(role)}</span>
        </div>
        <button className="btn" onClick={onSignOut}>Sign out</button>
      </div>
    </aside>
  )
}

function roleLabel(role) {
  return {
    ADMIN: 'Administrator',
    MANAGEMENT: 'Management — read only',
    SAFETY_OFFICER: 'Safety officer',
    ELECTRICAL: 'Electrical engineer',
    MECHANICAL: 'Mechanical engineer',
  }[role] || role || ''
}

function SignIn({ onDone }) {
  const [staff, setStaff] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function submit(e) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const role = await login(staff.trim(), password)
      // An OPERATOR or VENDOR account is valid but has nothing here: their work is the Android app, and every
      // console endpoint would refuse them. Saying so beats signing them into an empty shell.
      if (role === 'OPERATOR' || role === 'VENDOR') {
        await logout()
        setError({ message: 'This account is for the mobile app. The console is for supervisors, engineers, safety officers and management.' })
        return
      }
      onDone(role)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="entrance">
      {/*
        Two panels: the plant on the left, the form on the right.

        The illustration is not decoration — it says what this system is for before a word is read, and it is
        drawn geometry rather than a stock photograph so it costs nothing to load, scales to any plant display,
        and uses the exact SAIL tokens instead of approximating them. Below 900px it collapses to a banner
        above the form rather than disappearing, because the identity is the point of this screen.
      */}
      {/*
        A real plant photograph, supplied for this application: an EOT crane carrying a steel coil with the
        condition-monitoring overlay that is the point of the system. It replaces the SVG I drew — a real
        crane in a real bay says more than geometry can, and it is the plant's own imagery rather than an
        approximation of it.

        The caption is one line. The longer marketing sentence that sat here was cut on request, and rightly:
        the people signing in maintain these cranes for a living and do not need the product explained to
        them.
      */}
      <section className="entrance-art">
        <img
          className="entrance-photo"
          src="/console/brand/hero-crane.jpg"
          alt="Overhead travelling crane carrying a steel coil, with condition-monitoring sensors overlaid"
        />
        <div className="entrance-copy">
          <p className="entrance-eyebrow">Industry 4.0 · Smart Manufacturing</p>
          <h2 className="entrance-caption">
            Crane Maintenance &amp; Management System
          </h2>
        </div>
      </section>

      <section className="entrance-form">
        <form className="signin" onSubmit={submit}>
          <div className="signin-brand">
            <SailMark height={40} />
          </div>

          <div>
            <h1>Crane Maintenance &amp; Management System</h1>
          </div>

          {error && <Banner kind="danger">{error.message}</Banner>}

          <Field label="Staff number" id="staff">
            <input id="staff" value={staff} onChange={(e) => setStaff(e.target.value)} autoComplete="username" autoFocus required />
          </Field>
          <Field label="Password" id="pw">
            <input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
          </Field>

          <Button type="submit" disabled={busy || !staff || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>

          <p className="muted signin-foot">
            Operators and vendors use the Android app.
          </p>
        </form>
      </section>
    </div>
  )
}

export default function App() {
  const [role, setRole] = useState(session.role)
  const [signedIn, setSignedIn] = useState(session.signedIn)
  const location = useLocation()
  const navigate = useNavigate()

  /*
   * A 401 from any page clears the session in `api.js`; this notices and drops back to sign-in rather than
   * leaving a shell full of error states. Polling `sessionStorage` is deliberate over an event bus: the token
   * can also be cleared by another tab or by the refresh failing mid-request, and a 2s check is cheaper than
   * threading a callback through every page.
   */
  useEffect(() => {
    const t = setInterval(() => {
      if (signedIn && !session.signedIn) {
        setSignedIn(false)
        setRole(null)
      }
    }, 2000)
    return () => clearInterval(t)
  }, [signedIn])

  if (!signedIn) {
    return (
      <SignIn
        onDone={(r) => {
          setRole(r)
          setSignedIn(true)
          navigate('/')
        }}
      />
    )
  }

  return (
    <div className="shell">
      <Sidebar
        role={role}
        onSignOut={async () => {
          await logout()
          setSignedIn(false)
          setRole(null)
        }}
      />
      <main className="content" key={location.pathname}>
        {/*
          Keyed on the path, so a crash on one page does not follow the user to the next.

          A React error boundary latches once it catches: without re-keying, clicking a working nav link would
          still show the error, making one broken page look like a broken console. The `key` remounts the
          boundary on navigation, which is the recovery path.
        */}
        <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="/" element={<Dashboard role={role} />} />
          <Route path="/reliability" element={<Reliability />} />
          <Route path="/failures" element={<FailureAnalysis />} />
          <Route path="/cranes" element={<Cranes />} />
          <Route path="/cranes/:id" element={<CraneDetail />} />
          <Route path="/complaints" element={<Complaints />} />
          <Route path="/incidents" element={<Incidents />} />
          {/* Not role-guarded in the router, unlike everything under /admin. The register and the report
              download are readable by every signed-in role by decision — the report endpoint is
              `isAuthenticated()` — and the scheduling half of the page is refused by the server to anyone but
              SAFETY_OFFICER and ADMIN, which is where that rule belongs. */}
          <Route path="/inspections" element={<Inspections />} />
          {/* Not role-guarded here either: the server admits five roles and refuses the two that cannot reach
              the console at all, so a router guard would only duplicate — and eventually contradict — it. */}
          <Route path="/reports" element={<Reports />} />
          {/* Guarded in the router as well as the sidebar: a MANAGEMENT user who types /admin gets sent home
              rather than a page of 403s. The server still refuses the calls regardless. */}
          <Route path="/admin" element={role === 'ADMIN' ? <Admin /> : <Navigate to="/" replace />} />
          {/* Native now, not links to the old pages: an administrator signing in six times to do one round of
              administration was the single loudest complaint about this surface. */}
          <Route path="/admin/users" element={role === 'ADMIN' ? <Users /> : <Navigate to="/" replace />} />
          <Route path="/admin/vendors" element={role === 'ADMIN' ? <Vendors /> : <Navigate to="/" replace />} />
          {/* The last four tools, native. Nothing under /admin now leaves the console or asks for a second
              sign-in — which was the complaint that started this. */}
          <Route path="/admin/cranes" element={role === 'ADMIN' ? <CraneEntry /> : <Navigate to="/" replace />} />
          <Route path="/admin/import" element={role === 'ADMIN' ? <CraneImport /> : <Navigate to="/" replace />} />
          <Route path="/admin/escalation" element={role === 'ADMIN' ? <Escalation /> : <Navigate to="/" replace />} />
          {/* PM authoring. The endpoint has existed and been admin-gated since V1 with no client at all, so
              prod could never hold a schedule and every engineer's Work tab read "Nothing due". */}
          <Route path="/admin/pm" element={role === 'ADMIN' ? <PlannedMaintenance /> : <Navigate to="/" replace />} />
          {/* API docs is not ADMIN-gated: an engineer or safety officer reading the contract harms nothing,
              and every endpoint it describes keeps its own role check. */}
          <Route path="/admin/api" element={<ApiDocs />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
      </main>
    </div>
  )
}
